import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Pencil, Wand2, X } from "lucide-react";

import { getMathUnitProgress, patchMathRemediationRow } from "../lib/api";

/**
 * Math Engine — Adaptive Plan Preview (Phase 91, exposed Phase 91b).
 *
 * Renders the structured remediation list Claude derived from the
 * teacher's mid-unit check-in summary. Shown on the exit-review screen
 * BEFORE the teacher clicks "Generate Lesson N", so they can see what
 * the engine plans to revisit (re-teach) or stretch (extend) inside the
 * next adaptive lesson.
 *
 * Phase 105.3 — per-row inline edits. Teachers can click any chip to
 * cycle status (re_teach → hold_ground → extend) and click the note
 * text to edit it in place. Edits save optimistically via
 * PATCH /math-engine/units/remediation-row and revert on error.
 *
 * Renders nothing when the unit has no remediation logged yet —
 * the rest of the exit-review UI remains unchanged.
 *
 * Props:
 *   - unitId: e.g. "Grade 5 — Unit 2 — Number"
 */
const STATUS_PRESENTATION = {
  re_teach: {
    label: "Re-teach",
    chip: "bg-rose-100 text-rose-900 border border-rose-300",
    chipHover: "hover:bg-rose-200",
    description: "Built into this lesson as a brief revisit",
  },
  hold_ground: {
    label: "Hold ground",
    chip: "bg-amber-100 text-amber-900 border border-amber-300",
    chipHover: "hover:bg-amber-200",
    description: "Quick consolidation practice",
  },
  extend: {
    label: "Extend",
    chip: "bg-emerald-100 text-emerald-900 border border-emerald-300",
    chipHover: "hover:bg-emerald-200",
    description: "Stretch task for the ready group",
  },
};
const STATUS_ORDER = ["re_teach", "hold_ground", "extend"];

const EditableNote = ({ row, busy, onSave }) => {
  // Notion-style click-to-edit. Click the note → swap to a controlled
  // textarea. Enter saves, Shift+Enter inserts a newline, Esc reverts.
  // Blur saves too so teachers don't lose a click-away edit.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(row.note || "");
  const inputRef = useRef(null);
  const enterEdit = () => {
    setDraft(row.note || "");
    setEditing(true);
  };
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);
  const commit = async () => {
    const next = draft.trim();
    if (next === (row.note || "").trim()) {
      setEditing(false);
      return;
    }
    const ok = await onSave({ note: next });
    if (ok) setEditing(false);
    else setDraft(row.note || "");
  };
  const cancel = () => {
    setDraft(row.note || "");
    setEditing(false);
  };
  if (editing) {
    return (
      <span className="inline-flex items-start gap-1.5 flex-1 min-w-0">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              cancel();
            }
          }}
          rows={2}
          disabled={busy}
          maxLength={120}
          data-testid={`math-adaptive-note-input-${row.code}`}
          className="flex-1 min-w-0 text-sm leading-snug text-amber-950 bg-white border border-amber-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
        />
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={enterEdit}
      disabled={busy}
      data-testid={`math-adaptive-note-${row.code}`}
      className="text-left text-sm leading-snug text-amber-950 hover:bg-amber-100/80 rounded px-1 py-0.5 -mx-1 -my-0.5 transition flex-1 min-w-0 group inline-flex items-start gap-1"
      title="Click to edit note"
    >
      <span className="flex-1">
        {row.note || (
          <span className="italic text-amber-900/60">
            Click to add a note…
          </span>
        )}
      </span>
      <Pencil
        className="h-3 w-3 text-amber-700 shrink-0 opacity-0 group-hover:opacity-100 mt-1 transition"
        aria-hidden
      />
    </button>
  );
};

const StatusChip = ({ row, busy, onCycle }) => {
  const pres = STATUS_PRESENTATION[row.status] || STATUS_PRESENTATION.hold_ground;
  return (
    <button
      type="button"
      onClick={() => onCycle()}
      disabled={busy}
      title="Click to change status"
      data-testid={`math-adaptive-status-${row.code}`}
      className={`text-[10px] font-extrabold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded shrink-0 mt-0.5 transition ${pres.chip} ${pres.chipHover} disabled:opacity-50 cursor-pointer`}
    >
      {row.code}
    </button>
  );
};

const AdaptivePlanPreview = ({ unitId }) => {
  const [remediation, setRemediation] = useState(null); // null = loading, [] = empty
  const [error, setError] = useState(null);
  // Per-row save state — `null` idle, `"saving"` in flight, `"saved"` brief
  // confirmation pulse, `"error"` reverted with an inline message.
  const [rowState, setRowState] = useState({});

  useEffect(() => {
    if (!unitId) return;
    let cancelled = false;
    (async () => {
      try {
        const prog = await getMathUnitProgress(unitId);
        if (cancelled) return;
        setRemediation(prog?.mid_quiz?.remediation || []);
      } catch (e) {
        if (cancelled) return;
        // Quietly hide — this preview is an enhancement, not a hard
        // requirement for the exit-review flow.
        setError(e?.message || "Could not load adaptive plan");
        setRemediation([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unitId]);

  if (remediation === null) {
    return (
      <div
        className="rounded-xl border border-amber-300/60 bg-amber-50/40 px-4 py-3 text-xs text-amber-900/80 flex items-center gap-2"
        data-testid="math-adaptive-preview-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking for adaptive context…
      </div>
    );
  }
  if (!remediation.length || error) return null;

  const patchRow = async (code, patch) => {
    // Optimistic update — flip local state immediately, fire the
    // PATCH, then either confirm (state stays) or revert to the
    // pre-edit row on error.
    const before = remediation.find((r) => r.code === code);
    if (!before) return false;
    setRowState((s) => ({ ...s, [code]: "saving" }));
    setRemediation((rows) =>
      rows.map((r) => (r.code === code ? { ...r, ...patch } : r)),
    );
    try {
      const resp = await patchMathRemediationRow(unitId, code, patch);
      if (Array.isArray(resp?.remediation)) {
        setRemediation(resp.remediation);
      }
      setRowState((s) => ({ ...s, [code]: "saved" }));
      // Clear the "saved" pulse after a beat.
      setTimeout(
        () => setRowState((s) => ({ ...s, [code]: null })),
        1200,
      );
      return true;
    } catch (e) {
      // Revert to the pre-edit row state.
      setRemediation((rows) =>
        rows.map((r) => (r.code === code ? before : r)),
      );
      setRowState((s) => ({ ...s, [code]: "error" }));
      setTimeout(
        () => setRowState((s) => ({ ...s, [code]: null })),
        2200,
      );
      // Surface the error briefly via console so the developer can
      // diagnose — the inline icon already tells the teacher.
      // eslint-disable-next-line no-console
      console.warn("patchRemediationRow failed", e);
      return false;
    }
  };

  const cycleStatus = (row) => {
    const idx = STATUS_ORDER.indexOf(row.status);
    const next = STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
    patchRow(row.code, { status: next });
  };

  // Group entries by status, preserving STATUS_ORDER.
  const grouped = STATUS_ORDER.map((status) => ({
    status,
    rows: remediation.filter((r) => r.status === status),
  })).filter((g) => g.rows.length > 0);

  return (
    <section
      data-testid="math-adaptive-preview"
      className="rounded-xl border-2 border-amber-300 bg-amber-50/70 px-5 py-4 space-y-3"
    >
      <header className="flex items-start gap-2">
        <Wand2 className="h-5 w-5 text-amber-900 shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-amber-900/80">
            Adaptive context active
          </p>
          <h4 className="font-extrabold text-amber-950 text-base leading-tight">
            What the next lesson will adjust
          </h4>
          <p className="text-xs text-amber-900/80 mt-0.5">
            Derived from your mid-unit check-in results. Click any chip to change
            its status (re-teach / hold ground / extend) or click a note to edit
            it in place — the next lesson will pick up your changes.
          </p>
        </div>
      </header>
      <div className="space-y-2">
        {grouped.map(({ status, rows }) => {
          const pres = STATUS_PRESENTATION[status];
          return (
            <div
              key={status}
              data-testid={`math-adaptive-preview-group-${status}`}
              className="rounded-lg bg-white/70 border border-amber-200 p-3"
            >
              <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] text-amber-900/80 mb-1.5">
                {pres.label}{" "}
                <span className="font-medium text-amber-900/60 normal-case tracking-normal">
                  · {pres.description}
                </span>
              </p>
              <ul className="space-y-1">
                {rows.map((row) => {
                  const busy = rowState[row.code] === "saving";
                  return (
                    <li
                      key={row.code}
                      data-testid={`math-adaptive-preview-row-${row.code}`}
                      className="flex items-start gap-2 text-sm text-amber-950"
                    >
                      <StatusChip
                        row={row}
                        busy={busy}
                        onCycle={() => cycleStatus(row)}
                      />
                      <EditableNote
                        row={row}
                        busy={busy}
                        onSave={(patch) => patchRow(row.code, patch)}
                      />
                      {/* Save state indicator — saving spinner, saved
                          check pulse, error X. */}
                      {rowState[row.code] === "saving" && (
                        <Loader2
                          className="h-3.5 w-3.5 animate-spin text-amber-700 shrink-0 mt-0.5"
                          data-testid={`math-adaptive-saving-${row.code}`}
                        />
                      )}
                      {rowState[row.code] === "saved" && (
                        <Check
                          className="h-3.5 w-3.5 text-emerald-700 shrink-0 mt-0.5"
                          data-testid={`math-adaptive-saved-${row.code}`}
                        />
                      )}
                      {rowState[row.code] === "error" && (
                        <X
                          className="h-3.5 w-3.5 text-rose-700 shrink-0 mt-0.5"
                          data-testid={`math-adaptive-error-${row.code}`}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AdaptivePlanPreview;
